import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { PageNavigationView } from '@/shared/components/page-navigation/page-navigation';
import { Badge } from '@/shared/components/shadcn/badge';
import { Button } from '@/shared/components/shadcn/button';
import { Input } from '@/shared/components/shadcn/input';
import { Table, TableBody, TableCell, TableRow } from '@/shared/components/shadcn/table';
import { cn } from '@/shared/utils';

import { getPostCountBadge } from '../../../helpers/campaign-create-ux';
import {
  CAMPAIGN_CREATE_TABLE_PAGE_SIZE,
  focusElement,
} from '../../../hooks';
import type { CampaignPostRows } from './hooks';
import type { CampaignPostValidation } from './hooks';

const TABLE_GRID = {
  post: 'minmax(280px,1fr) 56px',
};

type Props = {
  rows: CampaignPostRows;
  validation: Pick<CampaignPostValidation, 'postIssues'>;
};

export function CampaignPostInputCanvas({ rows, validation }: Props) {
  const [postTablePage, setPostTablePage] = useState(1);
  const [pendingFocusRowId, setPendingFocusRowId] = useState<string>();
  const filledPostCount = rows.items.filter((item) => item.postUrl.trim().length > 0).length;
  const postCountBadge = getPostCountBadge(filledPostCount);
  const hasFilledRows = rows.items.some((item) => item.postUrl.trim().length > 0);
  const totalPages = Math.max(
    1,
    Math.ceil(rows.items.length / CAMPAIGN_CREATE_TABLE_PAGE_SIZE),
  );
  const shouldPaginate = rows.items.length > CAMPAIGN_CREATE_TABLE_PAGE_SIZE;
  const pageStartIndex = (postTablePage - 1) * CAMPAIGN_CREATE_TABLE_PAGE_SIZE;
  const paginatedItems = rows.items.slice(
    pageStartIndex,
    pageStartIndex + CAMPAIGN_CREATE_TABLE_PAGE_SIZE,
  );

  useEffect(() => {
    setPostTablePage((currentPage) => Math.min(currentPage, totalPages));
  }, [totalPages]);

  useEffect(() => {
    if (!pendingFocusRowId) return;
    const target = rows.urlRefs.current[pendingFocusRowId];
    if (!target) return;
    requestAnimationFrame(() => {
      focusElement(target);
    });
    setPendingFocusRowId(undefined);
  }, [pendingFocusRowId, rows.items, rows.urlRefs]);

  const handleAddMoreClick = () => {
    const targetRowId = rows.add();
    if (!targetRowId) return;

    const existingIndex = rows.items.findIndex((item) => item.id === targetRowId);
    const nextIndex = existingIndex >= 0 ? existingIndex : rows.items.length;
    setPostTablePage(Math.floor(nextIndex / CAMPAIGN_CREATE_TABLE_PAGE_SIZE) + 1);
    setPendingFocusRowId(targetRowId);
  };

  return (
    <div className="space-y-4">
      <div className="border-border bg-muted overflow-hidden rounded-lg border">
        <div className="border-border flex items-center justify-between gap-3 border-b px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="text-foreground text-sm font-medium">Post URLs list</div>
            <Badge
              className={cn(
                'rounded-full px-2.5 py-1 text-xs font-medium',
                'bg-primary text-primary-foreground hover:bg-primary border-transparent',
              )}
            >
              {postCountBadge}
            </Badge>
          </div>
          <button
            type="button"
            onClick={rows.clear}
            disabled={!hasFilledRows}
            className="text-muted-foreground hover:text-foreground disabled:text-muted-foreground/50 text-sm font-medium transition-colors disabled:pointer-events-none"
          >
            Clear all
          </button>
        </div>

        <div className="px-4 py-3">
          <div className="overflow-x-auto">
            <Table className="min-w-90">
              <TableBody>
                {paginatedItems.map((item) => {
                  const rowIssue = validation.postIssues.find(
                    (issue) => issue.rowId === item.id,
                  )?.message;
                  return (
                    <TableRow
                      key={item.id}
                      gridTemplateColumns={TABLE_GRID.post}
                      className="items-start"
                    >
                      <TableCell className="flex-col items-stretch gap-1.5 px-3 py-3">
                        <Input
                          ref={(element) => {
                            rows.urlRefs.current[item.id] = element;
                          }}
                          value={item.postUrl}
                          onChange={(event) =>
                            rows.actions.changeUrl(item.id, event.target.value)
                          }
                          onBlur={() => rows.actions.normalizeUrl(item.id)}
                          placeholder="https://www.instagram.com/reel/ABC123DEF45"
                          className="bg-card h-9 px-2.5 text-sm"
                        />
                        {rowIssue ? <p className="text-destructive text-xs">{rowIssue}</p> : null}
                      </TableCell>
                      <TableCell className="justify-end px-2 py-3">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => rows.actions.remove(item.id)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>

        <div className="border-border flex items-center justify-between gap-3 border-t px-4 py-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleAddMoreClick}
            disabled={rows.remainingSlots === 0}
            className="text-muted-foreground hover:text-foreground h-auto px-0 text-sm font-medium hover:bg-transparent"
          >
            <Plus className="size-4" />
            Add more
          </Button>

          {shouldPaginate ? (
            <PageNavigationView
              className="p-0"
              startIndex={pageStartIndex + 1}
              endIndex={Math.min(
                pageStartIndex + CAMPAIGN_CREATE_TABLE_PAGE_SIZE,
                rows.items.length,
              )}
              totalCount={rows.items.length}
              onPrevious={() => setPostTablePage((page) => Math.max(page - 1, 1))}
              onNext={() => setPostTablePage((page) => Math.min(page + 1, totalPages))}
              canGoPrevious={postTablePage > 1}
              canGoNext={postTablePage < totalPages}
            />
          ) : (
            <div />
          )}
        </div>
      </div>
    </div>
  );
}
